import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import GameRoom from "./GameRoom";
import UserContext from "../../context/UserContext";
import SocketContext from "../../context/SocketContext";
import GameRoomContext from "../../context/GameRoomContext";
import UIContext from "../../context/UIContext";
import sanitizeInput from "../../hooks/sanitizeInput";
import analytics, { hashId } from '../../utils/analytics';

function GameRoomContainer({ gameRoomData }) {
  const { user } = useContext(UserContext);
  const socket = useContext(SocketContext);
  const ui = useContext(UIContext);
  const openSystemModalRef = useRef(ui?.openSystemModal);
  const exitToLobbyRef = useRef(ui?.exitToLobby);
  const setActiveLeaveRoomHandler = ui?.setActiveLeaveRoomHandler;

  const roomId = gameRoomData?.gameRoomDetails?.roomId || "";
  const [readyPlayers, setReadyPlayers] = useState(
    gameRoomData?.gameRoomGame?.readyPlayers || []
  );
  const [hostUserId, setHostUserId] = useState("");

  useEffect(() => {
    setReadyPlayers(gameRoomData?.gameRoomGame?.readyPlayers || []);
    setHostUserId(gameRoomData?.gameRoomDetails?.hostUserId || "");
  }, [gameRoomData]);

  useEffect(() => {
    openSystemModalRef.current = ui?.openSystemModal;
    exitToLobbyRef.current = ui?.exitToLobby;
  }, [ui?.openSystemModal, ui?.exitToLobby]);

  // Keep local state used by handlers
  const [roomMessages, setRoomMessages] = useState([]);
  const [gameRoomTypingPlayers, setGameRoomTypingPlayers] = useState([]);
  const [, setGameRoomPlayers] = useState([]);
  // Keep container state (some values are mutated by handlers even if not read locally)
  const [, setGamePlayers] = useState([]);
  const [, setGameState] = useState(null);
  const [, setGameConfig] = useState({});
  const [, setConfigSchema] = useState([]);
  const [, setGameType] = useState("");
  const [, setRoomName] = useState("");
  const [, setGameRoomMessages] = useState([]);
  const [, setGameRoomConfig] = useState({});
  const [, setGameRoomState] = useState(null);

  // Game room state

  // Room joined
  const handleRoomJoined = (data) => {
    // setRoomId(data.roomId);
    // try {
    //   localStorage.setItem("lastRoomId", data.roomId);
    // } catch {}
    // setRoomName(data.roomName);
    // setGamePlayers(data.players || []);
    // setGameState(data.gameState || null);
    // setHostId(data.hostId || "");
    // setGameConfig(data.config || {});
    // setConfigSchema(data.configSchema || []);
    // setRoomMessages(data.messages || []);
    // setGameType(data.gameType || "");
    // setReadyPlayers(data.readyPlayers || []);
    //goToGameRoom();
  };

  // Room state update (after config/player changes)
  const handleRoomState = (data) => {
    // setRoomId(data.roomId);
    // try {
    //   localStorage.setItem("lastRoomId", data.roomId);
    // } catch {}
    // setRoomName(data.roomName);
    // setGamePlayers(data.players || []);
    // // Force a new object reference for gameState to ensure React re-renders
    // setGameState(
    //   data.gameState ? JSON.parse(JSON.stringify(data.gameState)) : null
    // );
    // setHostId(data.hostId || "");
    // setGameConfig(data.config || {});
    // setConfigSchema(data.configSchema || []);
    // setRoomMessages(data.messages || []);
    // setGameType(data.gameType || "");
    // setReadyPlayers(data.readyPlayers || []);
    // setGameSpectators(data.gameSpectators || data.spectators || []);
    // setPlayersCanJoinAnytime(data.playersCanJoinAnytime || false);
    //goToGameRoom();
  };

  // Host left handler
  const handleHostLeft = useCallback((data) => {
    // If the host ends the game, non-host players should be returned to the lobby.
    // Clear local room state and call UI context to show lobby.
    try {
      localStorage.removeItem("lastRoomId");
    } catch (e) {}
    setRoomName("");
    setGamePlayers([]);
    setGameState(null);
    setHostUserId("");
    setGameConfig({});
    setConfigSchema([]);
    setRoomMessages([]);
    setGameType("");
    setReadyPlayers([]);
    setGameRoomPlayers([]);
    setGameRoomMessages([]);
    setGameRoomTypingPlayers([]);
    setGameRoomConfig({});
    setGameRoomState(null);

    // Show a modal to explain why the user was returned, then switch to lobby
    if (typeof openSystemModalRef.current === "function") {
      openSystemModalRef.current(
        data?.message || "The host has left the game room so it has been closed down",
        "Room closed"
      );
    }
    if (typeof exitToLobbyRef.current === "function") {
      exitToLobbyRef.current();
    }
  }, []);

  // useEffect(() => {
  //   roomId = gameRoomData?.gameRoomDetails?.roomId || "";
  // }, [roomId, gameRoomData]);

  // Room chat (single message)
  const handleRoomChat = useCallback(({ roomId: incomingRoomId, message }) => {
    // Only append if we're in this room
    if (roomId && incomingRoomId === roomId) {
      setRoomMessages((prev) => [...prev, message]);
    }
  }, [roomId]);

  const leaveRoom = useCallback(() => {
    const exitToLobby = () => {
      if (typeof exitToLobbyRef.current === "function") {
        exitToLobbyRef.current();
      }
    };

    if (!roomId) {
      exitToLobby();
      return;
    }

    try {
      localStorage.removeItem("lastRoomId");
    } catch {}

    if (!socket || !socket.connected) {
      exitToLobby();
      return;
    }

    socket.emit("leaveRoom", { roomId });

    window.setTimeout(() => {
      exitToLobby();
    }, 1200);
  }, [roomId, socket]);

  useEffect(() => {
    if (typeof setActiveLeaveRoomHandler !== "function") return undefined;

    setActiveLeaveRoomHandler(() => leaveRoom);

    return () => {
      setActiveLeaveRoomHandler(null);
    };
  }, [setActiveLeaveRoomHandler, leaveRoom]);

  // Typing indicators
  // Local emitter: called by local input handlers to update local list and notify server
  const handleGameRoomChatInputChange = useCallback(({ username: typingUser, isTyping }) => {
    setGameRoomTypingPlayers((prev) => {
      if (isTyping) {
        if (!prev.includes(typingUser) && typingUser !== user.username) {
          return [...prev, typingUser];
        }
      } else {
        return prev.filter((u) => u !== typingUser);
      }
      return prev;
    });
    // Emit typing indicator to server for other room members
    try {
      if (socket && roomId) {
        socket.emit("gameRoomTyping", { roomId, username: typingUser, isTyping });
      }
    } catch (e) {}
  }, [socket, roomId, user?.username]);

  // Incoming-only handler: used for socket events from other clients. Do NOT re-emit.
  const handleIncomingGameRoomTyping = useCallback(({ username: typingUser, isTyping }) => {
    setGameRoomTypingPlayers((prev) => {
      if (isTyping) {
        if (!prev.includes(typingUser) && typingUser !== user.username) {
          return [...prev, typingUser];
        }
      } else {
        return prev.filter((u) => u !== typingUser);
      }
      return prev;
    });
  }, [user?.username]);

  // Player joined
  const handlePlayerJoined = (player) => {
    setGamePlayers((prev) => {
      if (!prev.some((p) => p.id === player.id)) {
        return [...prev, player];
      }
      return prev;
    });
    setRoomMessages((prev) => [
      ...prev,
      {
        type: "event",
        text: `${player.username} joined the room.`,
        datetime: new Date().toISOString(),
        username: "System",
      },
    ]);
  };

  const handleGameRoomChatSend = (text) => {
    if (roomId) {
      socket.emit("sendRoomMessage", {
        roomId,
        message: {
          type: "chat",
          text: sanitizeInput(text),
          datetime: new Date().toISOString(),
          username: sanitizeInput(user.username) || "Unknown",
          color: user.color,
          icon: user.icon,
        },
      });
    }
  };

  const onGameAction = (action) => {
    if (roomId) socket.emit("gameAction", { roomId, action });
  };

  // Auto-rejoin logic: on mount, if lastRoomId is present and not already joined, try to rejoin
  // Auto-rejoin is handled at App level to avoid duplicate join attempts.

  const onUpdateGameConfig = (newConfig) => {
    setGameConfig(newConfig);
    // Emit update only if current client is host. Check both persistent userId and ephemeral socket id.
    const hostSocketId = gameRoomData?.gameRoomDetails?.hostSocketId;
    const hostUid = gameRoomData?.gameRoomDetails?.hostUserId;
    const amHost =
      (hostUid && hostUid === (user?.userId || socket.auth?.userId)) ||
      (hostSocketId && hostSocketId === socket.id);
    if (roomId && amHost) {
      socket.emit("updateGameConfig", { roomId, config: newConfig });
    }
  };

  const onStartGame = () => {
    // Read min/max from the server-synced gameRoomConfigSchema (fall back to sensible defaults)
    let minPlayers = 2;
    let maxPlayers = Infinity;
    const serverSchema = gameRoomData?.gameRoomConfigSchema || {};
    if (serverSchema && typeof serverSchema.minPlayers === "number") {
      minPlayers = serverSchema.minPlayers;
    }
    if (serverSchema && typeof serverSchema.maxPlayers === "number") {
      maxPlayers = serverSchema.maxPlayers;
    }
    // Ensure gameRoomPlayers is an array before checking length
    const playersArr = Array.isArray(gameRoomData?.gameRoomPlayers)
      ? gameRoomData.gameRoomPlayers
      : [];
    if (
      roomId &&
      hostUserId === socket.auth?.userId &&
      playersArr.length >= minPlayers &&
      playersArr.length <= maxPlayers
    ) {
      socket.emit("startGame", { roomId });

      const playerCount = playersArr.length || 0;
      const gameName = gameRoomData.gameRoomConfigSchema.displayName || 'unknown';

      analytics.trackGameStarted({
            gameType: gameName,
            roomId,
            player_count: playerCount,
            userId: hostUserId || socket.auth?.userId || null,
            region: user?.country || (navigator.language ? navigator.language.split('-')[1] : undefined)
          });

    }
  };

  const onPlayAgain = (config) => {
    if (roomId) {
      socket.emit("playAgain", { roomId, config });

      const playerCount = gameRoomData.gameRoomPlayers.length || 0;
      const gameName = gameRoomData.gameRoomConfigSchema.displayName || 'unknown';

      analytics.trackGameStartedAgain({
            gameType: gameName,
            roomId,
            player_count: playerCount,
            userId: hostUserId || socket.auth?.userId || null,
            region: user?.country || (navigator.language ? navigator.language.split('-')[1] : undefined)
          });


    }
  };



  // Handler for changing game type
  // Allow optional selectedPlayerIds to be passed when host needs to remove players
  const onChangeGameType = (roomIdArg, newGameType, selectedPlayerIds) => {
    if (socket && roomIdArg) {
      const payload = { roomId: roomIdArg, newGameType };
      if (Array.isArray(selectedPlayerIds) && selectedPlayerIds.length > 0) {
        payload.selectedPlayerIds = selectedPlayerIds;
      }
      socket.emit("changeGameType", payload);
    }
  };

  // Handler for becoming spectator
  const onBecomeSpectator = (roomIdArg) => {
    if (socket && roomIdArg) {
      socket.emit("becomeSpectator", { roomId: roomIdArg });
    }
  };

  // Handler for becoming player
  const onBecomePlayer = (roomIdArg) => {
    if (socket && roomIdArg) {
      socket.emit("becomePlayer", { roomId: roomIdArg });
    }
  };

  const onToggleAllowSpectators = (roomIdArg, allowSpectators) => {
    if (socket && roomIdArg && typeof allowSpectators === "boolean") {
      socket.emit("setRoomAllowSpectators", { roomId: roomIdArg, allowSpectators });
    }
  };

  const onRoomParticipantAction = (roomIdArg, targetParticipantId, action) => {
    if (socket && roomIdArg && targetParticipantId && action) {
      socket.emit("roomParticipantAction", {
        roomId: roomIdArg,
        targetParticipantId,
        action,
      });
    }
  };

  const onLeaveNextRoundQueue = (roomIdArg) => {
    if (socket && roomIdArg) {
      socket.emit("leaveNextRoundQueue", { roomId: roomIdArg });
    }
  };

  // Ready/Unready logic for non-hosts - use persistent id when available
  const onTogglePlayerReady = () => {
    if (!roomId || !socket) return;
    const currentUserId = user?.userId || socket?.auth?.userId || socket.id || null;
    if (currentUserId && readyPlayers.includes(currentUserId)) {
      socket.emit("playerUnready", { roomId });
    } else {
      socket.emit("playerReady", { roomId });
    }
  };

  function getShareLink(roomId) {
    return new Promise((resolve) => {
      //goToShare();
      socket.emit("getShareLink", { roomId }, (link) => {
        resolve(link);
      });
    });
  }

  useEffect(() => {
    if (!socket) return;

    socket.on("changeGameTypeSuccess", ({ roomId, newGameType }) => {
      if (roomId) {
        //console.log("Game type changed:", newGameType);
      }
    });

    // If server forces a player removal (host removed player due to game change), handle it
    socket.on('removedFromRoom', ({ roomId: removedRoomId, reason }) => {
      try {
        if (removedRoomId === roomId) {
          if (typeof openSystemModalRef.current === 'function') {
            openSystemModalRef.current(reason || 'You have been removed from the room', 'Removed from Room');
          }
          if (typeof exitToLobbyRef.current === 'function') {
            exitToLobbyRef.current();
          }
        }
      } catch (e) {}
    });

    socket.on("updateGameConfigSuccess", () => {
      //console.log("Game config updated");
    });
    const handleRoomParticipantActionError = ({ error }) => {
      if (typeof openSystemModalRef.current === "function" && error) {
        openSystemModalRef.current(error, "Room action unavailable");
      }
    };

    // Socket event handling
    socket.on("roomJoined", handleRoomJoined);
    socket.on("roomState", handleRoomState);
    socket.on("hostLeft", handleHostLeft);
    const handleHostReclaimed = ({ hostSocketId, hostUserId }) => {
      // Debug: log receipt of hostReclaimed to detect duplicates
      try {
        // use console.debug to avoid noise in production tooling
        console.debug("[hostReclaimed] received", { roomId, hostSocketId, hostUserId, ts: new Date().toISOString() });
      } catch (e) {}
      setHostUserId(hostUserId || "");
      // setRoomMessages((prev) => [
      //   ...prev,
      //   {
      //     type: "event",
      //     text: "Host has reclaimed the room.",
      //     datetime: new Date().toISOString(),
      //     username: "System",
      //   },
      // ]);
    };
    const handleHostTransferred = ({ newHostSocketId, newHostUserId }) => {
      setHostUserId(newHostUserId || "");
      setRoomMessages((prev) => [
        ...prev,
        {
          type: "event",
          text: "Host has been transferred to another player.",
          datetime: new Date().toISOString(),
          username: "System",
        },
      ]);
    };
    socket.on("hostReclaimed", handleHostReclaimed);
    socket.on("hostTransferred", handleHostTransferred);
    socket.on("roomParticipantActionError", handleRoomParticipantActionError);
  socket.on("gameRoomTyping", handleIncomingGameRoomTyping);
    socket.on("playerJoined", handlePlayerJoined);
    socket.on("sendRoomMessageSuccess", handleRoomChat);

    return () => {
      socket.off("changeGameTypeSuccess");
      socket.off("roomJoined", handleRoomJoined);
      socket.off("roomState", handleRoomState);
      socket.off("hostLeft", handleHostLeft);
      socket.off("hostReclaimed", handleHostReclaimed);
      socket.off("hostTransferred", handleHostTransferred);
      socket.off("roomParticipantActionError", handleRoomParticipantActionError);
  socket.off("gameRoomTyping", handleIncomingGameRoomTyping);
      socket.off("playerJoined", handlePlayerJoined);
      socket.off("sendRoomMessageSuccess", handleRoomChat);
    };
  }, [socket, handleGameRoomChatInputChange, handleIncomingGameRoomTyping, handleRoomChat, handleHostLeft, roomId]);

  // Add socket event listeners and handlers here as needed
  // ...existing code...

  // Example: pass all state and handlers to GameRoom
  return (
    <>
      <GameRoomContext.Provider
        value={{
          gameRoomData,
          leaveRoom,
          roomMessages,
          gameRoomTypingPlayers,
          handleGameRoomChatInputChange,
          onGameAction,
          onChangeGameType,
          onPlayAgain,
          handleGameRoomChatSend,
          onTogglePlayerReady,
          onStartGame,
          onBecomePlayer,
          onLeaveNextRoundQueue,
          onBecomeSpectator,
          onRoomParticipantAction,
          onToggleAllowSpectators,
          getShareLink,
          onUpdateGameConfig,
        }}
      >
        <GameRoom />
      </GameRoomContext.Provider>
    </>
  );
}

export default GameRoomContainer;
